import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { RouteErrorBoundary } from '../ErrorBoundary';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireSuperAdmin?: boolean;
  requirePayment?: boolean;
  requireBuyer?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireSuperAdmin = false,
  requirePayment = true,
  requireBuyer = false
}) => {
  const { isAuthenticated, user, buyer, isBuyer, isSuperAdmin, hasActiveSubscription, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
          <p className="text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Buyer is logged in but trying to access a non-buyer route
  if (isBuyer && !requireBuyer) {
    return <Navigate to="/b2b" replace />;
  }

  // B2B route: allow buyers AND regular users (admins can view the B2B portal)
  if (requireBuyer && !isBuyer && !user) {
    return <Navigate to="/dashboard" replace />;
  }

  // Buyer routes are handled above, the rest applies to regular users
  if (!user && !buyer) {
    return <Navigate to="/login" replace />;
  }

  if (isSuperAdmin && !requireSuperAdmin) {
    return <Navigate to="/admin" replace />;
  }

  if (requireSuperAdmin && !isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requirePayment && !hasActiveSubscription && !isSuperAdmin) {
    if (user) {
      return <Navigate to="/activation-required" replace />;
    }
  }

  return <RouteErrorBoundary>{children}</RouteErrorBoundary>;
};